/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import { mergeListById } from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import MedicationFilter from "./Popup/MedicationFilter";
import SaveMedicationPopup from "components/Common/Popup/SaveMedicationPopup";

const MolecularMedicineSection = ({ patientObj = null }) => {
  const params = useParams();
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [medicationsList, setMedicationsList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedMedicationIds, setSelectedMedicationIds] = useState([]);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);

  // json filter query
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [reloadData, setReloadData] = useState(false);
  const [isFilterReset, setIsFilterReset] = useState(false);

  // get all medications
  const getAllMedications = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_MEDICINE +
        `?token=${token}&members=${params.id}`;

      if (jsonFilterQuery.length > 0) {
        // Sanitize the jsonFilterQuery
        const sanitizedQuery = jsonFilterQuery.map((filter) => {
          if (
            (filter.operator === "includes" ||
              filter.operator === "excludes") &&
            Array.isArray(filter.value)
          ) {
            return {
              ...filter,
              value: filter.value.map((item) => item.value).join(","), // Extract and join values as a comma-separated string
            };
          }
          return filter;
        });

        requestUrl = requestUrl + `&filters=${JSON.stringify(sanitizedQuery)}`;
      }

      console.log("requestUrl in get all medications", requestUrl);
      const response = await getData(requestUrl);
      setIsLoading(false);
      if (response.status) {
        setMedicationsList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // bulk update medications
  const updateBulkMedicationsHandler = async () => {
    if (selectedMedicationIds.length > 0) {
      try {
        const medicationsListToBeUpdate = medicationsList.filter((item) =>
          selectedMedicationIds.includes(item._id)
        );

        let medicationsData = {
          medicinelist: medicationsListToBeUpdate,
        };

        let requestURL =
          url.API_BASE_URL + url.API_UPDATE_BULK_MEDICINE + `?token=${token}`;

        const response = await putData(requestURL, medicationsData);

        console.log(response);

        if (response.status) {
          setTimeout(() => {
            setSelectedMedicationIds([]);
            setRowSelection({});
          }, 1500);
        }
      } catch (error) {
        console.log(error.message);
      }
    } else {
      alert("Please select at least one medication");
    }
  };

  // bulk delete medications
  const deleteBulkMedicationsHandler = async () => {
    if (selectedMedicationIds.length > 0) {
      try {
        const medicationsListToBeDelete = medicationsList.filter((item) =>
          selectedMedicationIds.includes(item._id)
        );

        let medicationsData = {
          medicinelist: medicationsListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL + url.API_DELETE_BULK_MEDICINE + `?token=${token}`;

        const response = await putData(requestURL, medicationsData);

        console.log(response);

        if (response.status) {
          setTimeout(() => {
            setMedicationsList((prevList) =>
              prevList.filter(
                (item) => !selectedMedicationIds.includes(item._id)
              )
            );
          }, 1500);
        }
      } catch (error) {
        console.log(error.message);
      }
    } else {
      alert("Please select at least one medication");
    }
  };

  // After any action
  const afterActionClose = (data = []) => {
    setMedicationsList((prevList) => mergeListById(prevList, data));
  };

  const resetFilterData = () => {
    setSelectedMedicationIds([]);
    setRowSelection({});
    setJsonFilterQuery([]);
    setIsFilterReset(true);
    setReloadData(true);
  };

  useEffect(() => {
    if (jsonFilterQuery.length > 0) {
      getAllMedications();
    }
  }, [jsonFilterQuery]);

  useEffect(() => {
    if (reloadData) {
      getAllMedications();
      setReloadData(false);
    }
  }, [reloadData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: "Actions",
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveMedicationOffCanvas"
            aria-controls="saveMedicationOffCanvas"
            onClick={() => {
              setSelectedMedicationId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Medicine",
        accessorKey: "productname",
        size: 200,
        Cell: ({ row }) => (
          <div
            className={
              row.original.productname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.productimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.productimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.productname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Start Date",
        accessorKey: "medicinestartdate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "End Date",
        accessorKey: "medicineenddate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "Recommended by",
        accessorKey: "referredpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.referredpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.referredpersonimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.referredpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.referredpersonname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Recommended For",
        accessorKey: "assignedpersonname",
        size: 200,
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
      //prescribed by
      // {
      //   header: "Prescribed by",
      //   accessorKey: "prescribedpersonname",
      //   size: 200,
      //   Cell: ({ row }) => (
      //     <div
      //       className={
      //         row.original.prescribedpersonname === ""
      //           ? "d-none"
      //           : "owner border-bottom-0 d-flex align-items-center gap-1"
      //       }
      //     >
      //       <img
      //         className="rounded-circle"
      //         height={35}
      //         width={35}
      //         src={
      //           row.original.prescribedpersonimage === ""
      //             ? assetImages.defaultUser
      //             : url.SERVER_URL + row.original.prescribedpersonimage
      //         }
      //         alt="User"
      //       />
      //       <p>
      //         <span className="d-block">
      //           {row.original.prescribedpersonname}
      //         </span>
      //       </p>
      //     </div>
      //   ),
      // },
      {
        header: "Direction / Usage",
        accessorKey: "comments",
        size: 200,
        Cell: ({ row }) => (
          <ul className="list-style-none p-0 m-0 d-flex flex-column gap-1">
            {row.original?.usagelist?.map((usage, index) => (
              <li key={index} className="text-gray fw-semibold">
                <span className="material-symbols-outlined icon-md text-danger me-1">
                  notifications_active
                </span>
                Dose {index + 1} : {usage.dose}mg at{" "}
                <span className="text-primary">{usage.time}</span>
              </li>
            ))}
          </ul>
        ),
      },
    ],
    []
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    if (selectedIdsArray.length > 0) {
      setSelectedMedicationIds(selectedIdsArray);
    } else {
      setSelectedMedicationIds([]);
    }
  }, [rowSelection]);

  return (
    <div className="p-4 bg-white h-100 rounded-10 shadow-sm">
      <div className="filter-container mb-4">
        <div className="row align-items-center mb-4">
          <div className="col-lg-4">
            <h3 className="mb-0">Medicines </h3>
          </div>
          <div className="col-lg-8 d-flex align-items-center justify-content-end gap-1 gap-sm-2">
            <Link to="#" className="btn btn-gray" onClick={resetFilterData}>
              <span className="d-block material-symbols-outlined">refresh</span>
            </Link>
            {/* <div className="dropdown flex-fill flex-grow-sm-0">
              <button
                className="btn btn-gray text-start w-100 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
              >
                Action
              </button>
              <ul className="dropdown-menu w-100 bg-white fs-sm fw-semibold border-0 rounded-10 shadow-sm">
                <li>
                  <Link
                    className={`dropdown-item d-flex align-items-center gap-2 py-2 ${
                      selectedMedicationIds.length === 0
                        ? "disabled text-muted"
                        : "text-danger"
                    }`}
                    to="#"
                    data-bs-toggle={
                      selectedMedicationIds.length === 0 ? undefined : "modal"
                    }
                    data-bs-target={
                      selectedMedicationIds.length === 0
                        ? undefined
                        : "#deletewarning"
                    }
                  >
                    <span className="d-block material-symbols-outlined icon-sm">
                      delete
                    </span>
                    <span className="d-block">{t("Delete")}</span>
                  </Link>
                </li>
              </ul>
            </div> */}
            <Link
              to="#"
              className="btn btn-gray"
              data-bs-toggle="offcanvas"
              data-bs-target="#medicationFilter"
              aria-controls="medicationFilter"
            >
              <span className="d-block material-symbols-outlined">tune</span>
            </Link>
            <Link
              to="#"
              className="btn btn-primary"
              data-bs-toggle="offcanvas"
              data-bs-target="#saveMedicationOffCanvas"
              aria-controls="saveMedicationOffCanvas"
            >
              Add New
            </Link>
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="table-wrapper">
          <MaterialReactTable
            columns={columns}
            data={medicationsList}
            enableGrouping // to enable grouping of column
            enableRowSelection // enable showing checkbox in each row
            getRowId={(row) => row._id}
            onRowSelectionChange={setRowSelection}
            state={{ rowSelection, columnOrder }}
            initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
            defaultColumn={{
              minSize: 20,
              maxSize: 300,
              size: 50, //make columns wider by default
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "60vh",
              },
            }}
            enableStickyHeader
          />
        </div>
      )}

      {/* popup and modals */}
      <SaveMedicationPopup
        patientObj={patientObj}
        selectedMedicationId={selectedMedicationId}
        setSelectedMedicationId={setSelectedMedicationId}
        afterActionClose={afterActionClose}
      />

      <MedicationFilter
        setJsonFilterQuery={setJsonFilterQuery}
        reloadList={resetFilterData}
        moduleSlug="molecularmedicine"
        isFilterReset={isFilterReset}
        setIsFilterReset={setIsFilterReset}
        columnOrder={columnOrder}
        columns={columns}
      />

      <DeleteWarningModal
        onDelete={deleteBulkMedicationsHandler}
        onCancelDelete={() => {
          setRowSelection({});
          setSelectedMedicationIds([]);
        }}
        moduleName={t("Medicines")}
      />
    </div>
  );
};

export default MolecularMedicineSection;
